import { useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Gavel, Sparkles, Loader2, ThumbsUp, ThumbsDown, Handshake } from "lucide-react";

const SEV = {
  low: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  medium: "border-orange-500/40 text-orange-300 bg-orange-500/10",
  high: "border-rose-500/40 text-rose-300 bg-rose-500/10",
};
const VERDICT = {
  accept: { class: "bg-emerald-500/10 border-emerald-500/40 text-emerald-300", icon: ThumbsUp, label: "Accept" },
  negotiate: { class: "bg-indigo-500/10 border-indigo-500/40 text-indigo-300", icon: Handshake, label: "Negotiate" },
  reject: { class: "bg-rose-500/10 border-rose-500/40 text-rose-300", icon: ThumbsDown, label: "Reject" },
};

export default function VendorNegotiatorPage() {
  const { propertyId } = useProperty();
  const [scope, setScope] = useState("");
  const [amount, setAmount] = useState("");
  const [terms, setTerms] = useState("");
  const [vendor, setVendor] = useState("");
  const [market, setMarket] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const analyze = async () => {
    if (!scope || !amount || !terms) return toast.error("Scope, amount, and terms are required");
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/ai/vendor-negotiator", {
        property_id: propertyId || "all",
        scope_of_work: scope,
        bid_amount: Number(amount),
        bid_terms: terms,
        vendor_name: vendor || null,
        comparable_market_range: market || null,
      });
      setResult(data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  const V = result?.verdict ? VERDICT[result.verdict] : null;
  const VIcon = V?.icon;

  return (
    <div>
      <PageHeader eyebrow="AI Features" title="Vendor Negotiator" subtitle="Score bids against market rates and generate a negotiation playbook the board can walk through with the vendor." />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-white/10 bg-card p-5" data-testid="negotiator-form">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-9 w-9 rounded-md bg-orange-500/10 border border-orange-500/30 flex items-center justify-center">
              <Gavel className="h-4 w-4 text-orange-300" />
            </div>
            <div className="text-lg font-semibold font-display">Bid input</div>
          </div>
          <div className="space-y-3">
            <div>
              <Label>Scope of work</Label>
              <Textarea rows={4} value={scope} onChange={(e) => setScope(e.target.value)} placeholder="e.g. Replace 8 rooftop HVAC units + 3-year maintenance contract" data-testid="neg-scope" className="mt-1 bg-white/[0.02] border-white/10" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Bid amount (USD)</Label>
                <Input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="45000" data-testid="neg-amount" className="mt-1 bg-white/[0.02] border-white/10" />
              </div>
              <div>
                <Label>Vendor name (optional)</Label>
                <Input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="Acme HVAC" data-testid="neg-vendor" className="mt-1 bg-white/[0.02] border-white/10" />
              </div>
            </div>
            <div>
              <Label>Bid terms</Label>
              <Textarea rows={5} value={terms} onChange={(e) => setTerms(e.target.value)} placeholder="Payment schedule, warranty, timeline, exclusions…" data-testid="neg-terms" className="mt-1 bg-white/[0.02] border-white/10" />
            </div>
            <div>
              <Label>Comparable market data (optional)</Label>
              <Textarea rows={3} value={market} onChange={(e) => setMarket(e.target.value)} placeholder="e.g. Similar HOA jobs in our zip: $38-42k median, 10-yr warranty" data-testid="neg-market" className="mt-1 bg-white/[0.02] border-white/10" />
            </div>
            <Button onClick={analyze} disabled={busy} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="neg-analyze">
              {busy ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Analyzing…</> : <><Sparkles className="h-3 w-3 mr-1" /> Analyze bid</>}
            </Button>
          </div>
        </Card>

        <Card className="border-white/10 bg-card p-5" data-testid="negotiator-result">
          {!result && !busy && (
            <div className="text-center py-12 text-sm text-zinc-500" data-testid="neg-empty">
              Fill in the bid on the left and click Analyze. Claude will score it and generate a playbook.
            </div>
          )}
          {busy && (
            <div className="text-center py-12 text-sm text-zinc-400 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Claude is analyzing the terms…
            </div>
          )}
          {result && (
            <div className="space-y-4">
              <div className={"rounded-lg border p-4 flex items-center justify-between " + (V?.class || "border-white/10")} data-testid="neg-verdict">
                <div>
                  <div className="text-[10px] uppercase tracking-widest opacity-80">Verdict</div>
                  <div className="text-2xl font-bold font-display mt-1 flex items-center gap-2">
                    {VIcon && <VIcon className="h-5 w-5" />} {V?.label || result.verdict}
                    <Badge variant="outline" className="border-white/20 text-[10px] uppercase tracking-widest ml-1">score {result.score}/100</Badge>
                  </div>
                </div>
              </div>

              <div className="text-sm text-zinc-300" data-testid="neg-summary">{result.one_line_summary}</div>
              <div className="text-xs text-zinc-500 border-l-2 border-white/10 pl-3 italic">{result.market_position}</div>

              {result.red_flags?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Red flags</div>
                  <div className="space-y-2">
                    {result.red_flags.map((f, i) => (
                      <div key={`${(f.issue || "").slice(0, 40)}-${i}`} className="rounded-md border border-white/10 bg-white/[0.02] p-3" data-testid={`neg-flag-${i}`}>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={SEV[f.severity]}>{f.severity}</Badge>
                          <div className="text-sm font-medium">{f.issue}</div>
                        </div>
                        <div className="text-xs text-zinc-400 mt-1">{f.why}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.playbook?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Negotiation playbook</div>
                  <div className="space-y-2">
                    {result.playbook.map((p, i) => (
                      <div key={`${(p.ask || "").slice(0, 40)}-${i}`} className="rounded-md border border-indigo-500/20 bg-indigo-500/[0.05] p-3" data-testid={`neg-play-${i}`}>
                        <div className="text-sm font-semibold text-indigo-200">Ask #{i + 1}: {p.ask}</div>
                        <div className="text-xs text-zinc-400 mt-1"><b>Why:</b> {p.rationale}</div>
                        <div className="text-xs text-zinc-400 mt-1"><b>Likely response:</b> {p.likely_response}</div>
                        <div className="text-xs text-zinc-400 mt-1"><b>Fallback:</b> {p.fallback}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.counter_offer && (
                <div className="rounded-md border border-emerald-500/30 bg-emerald-500/[0.05] p-3" data-testid="neg-counter">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1">Counter-offer target</div>
                  <div className="text-lg font-semibold text-emerald-200">${Number(result.counter_offer.target_amount || 0).toLocaleString()}</div>
                  <div className="text-xs text-zinc-400 mt-1">{result.counter_offer.justification}</div>
                  {result.counter_offer.target_terms?.length > 0 && (
                    <ul className="text-xs text-zinc-300 mt-2 space-y-0.5 list-disc list-inside">
                      {result.counter_offer.target_terms.map((t, i) => <li key={`term-${i}-${(typeof t === "string" ? t.slice(0, 32) : "")}`}>{t}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
